import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import type { Exam, ExamType } from '@proref/types';
import { PageHeader } from '../../components/shared/PageHeader';
import { RouteTabs } from '../../components/shared/RouteTabs';
import { Icon } from '../../components/shared/Icons';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { ListSkeleton } from '../../components/shared/Skeleton';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { DataPagination } from '../../components/shared/DataPagination';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess } from '../../lib/toast';
import { useAuth } from '../../contexts/auth-context';
import {
  useExamsQuery,
  useCreateExam,
  useDeleteExam,
  usePublishExam,
  useUnpublishExam,
} from '../../hooks/use-exams';
import { useSportsQuery } from '../../hooks/use-sports';
import { useFederationsQuery } from '../../hooks/use-federations';
import { usePagination } from '../../hooks/use-pagination';
import { FilterSelect, type FilterOption } from '../../components/shared/FilterSelect';

const EXAMS_PAGE_SIZE = 10;

// Per-type copy for the page header, empty state and the "New" button. Rule tests draw from the
// reusable question library; video tests draw from the federation's clip library — different
// enough authoring flows that they're now separate tabs rather than one type-column'd list.
// A function (not a static map) since the copy is translated.
function typeMeta(t: TFunction, type: ExamType): { tabLabel: string; title: string; sub: string; empty: string; newLabel: string } {
  return {
    tabLabel: t(`admin.exams.typeMeta.${type}.tabLabel`),
    title: t(`admin.exams.typeMeta.${type}.title`),
    sub: t(`admin.exams.typeMeta.${type}.sub`),
    empty: t(`admin.exams.typeMeta.${type}.empty`),
    newLabel: t(`admin.exams.typeMeta.${type}.newLabel`),
  };
}

function timeLimitLabel(t: TFunction, sec: number | null): string {
  if (sec === null) return t('admin.exams.timeLimit.untimed');
  return t('admin.exams.timeLimit.minutes', { min: Math.round(sec / 60) });
}

// "New exam" no longer opens a modal — it creates an empty DRAFT of whichever type this tab is
// (rule test or video test — no in-editor type switch to remember) and drops the admin straight
// onto the editor, where title, the Official toggle and questions are all set. A federation_admin's
// tenancy is JWT-derived; a super_admin's platform exam needs a sport, so we seed the first one
// (retargetable on the editor).
function NewExamButton({ examType }: { examType: ExamType }) {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isSuper = role === 'super_admin';
  const navigate = useNavigate();
  const createExam = useCreateExam();
  // super_admin only: a platform exam must name a sport up front (no modal to pick it), so seed the
  // first available and let them change it on the editor before adding questions.
  const sportsQuery = useSportsQuery(isSuper);
  const seedSportId = isSuper ? sportsQuery.data?.[0]?.id : undefined;
  const blocked = isSuper && !seedSportId; // super_admin with no sports yet can't author one

  async function create() {
    try {
      const created = await createExam.mutateAsync({
        title: t('admin.exams.untitled'),
        type: examType,
        ...(seedSportId ? { sport_id: seedSportId } : {}),
      });
      navigate(`/admin/exams/${created.id}/edit`);
    } catch (err) {
      notifyError(err, t('admin.exams.createErrorToast'));
    }
  }

  return (
    <Button variant="accent" onClick={create} disabled={createExam.isPending || blocked}>
      {createExam.isPending ? <Spinner className="w-4 h-4" /> : <Icon.plus className="w-3.5 h-3.5" />}{' '}
      {typeMeta(t, examType).newLabel}
    </Button>
  );
}

// Delete-confirm. The API refuses a published exam (409 EXAM_PUBLISHED) — surfaced via toast so
// the admin knows to unpublish first.
function DeleteExamDialog({ exam }: { exam: Exam }) {
  const { t } = useTranslation();
  const del = useDeleteExam();
  const [open, setOpen] = useState(false);

  async function confirm() {
    try {
      await del.mutateAsync(exam.id);
      notifySuccess(t('admin.exams.deleteDialog.deletedToast', { title: exam.title }));
      setOpen(false);
    } catch (err) {
      notifyError(err, t('admin.exams.deleteDialog.errorToast'));
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" icon aria-label={t('admin.exams.deleteDialog.deleteAria', { title: exam.title })}>
          <Icon.trash className="w-3.5 h-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.exams.deleteDialog.confirmTitle', { title: exam.title })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('admin.exams.deleteDialog.confirmBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={del.isPending}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <Button variant="danger" onClick={confirm} disabled={del.isPending}>
            {del.isPending ? <Spinner className="w-4 h-4" /> : t('admin.exams.deleteDialog.deleteButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Publish / unpublish toggle. Publishing can 400 (EXAM_NO_QUESTIONS) — the error surfaces via toast.
function PublishToggle({ exam }: { exam: Exam }) {
  const { t } = useTranslation();
  const publish = usePublishExam();
  const unpublish = useUnpublishExam();
  const isPublished = exam.published_at !== null;
  const pending = publish.isPending || unpublish.isPending;

  async function toggle() {
    try {
      if (isPublished) {
        await unpublish.mutateAsync(exam.id);
        notifySuccess(t('admin.exams.publishToggle.unpublishedToast', { title: exam.title }));
      } else {
        await publish.mutateAsync(exam.id);
        notifySuccess(t('admin.exams.publishToggle.publishedToast', { title: exam.title }));
      }
    } catch (err) {
      notifyError(
        err,
        isPublished ? t('admin.exams.publishToggle.unpublishErrorToast') : t('admin.exams.publishToggle.publishErrorToast'),
      );
    }
  }

  return (
    <Button variant="ghost" size="sm" onClick={toggle} disabled={pending}>
      {pending ? (
        <Spinner className="w-4 h-4" />
      ) : isPublished ? (
        t('admin.exams.publishToggle.unpublish')
      ) : (
        t('admin.exams.publishToggle.publish')
      )}
    </Button>
  );
}

// The federation filter's "no federation" value. A platform (Official) exam has federation_id
// null, which no uuid option can express — but it's the scope a super_admin authors most, so it
// needs to be pickable rather than only reachable by elimination.
const PLATFORM = 'platform';

function ExamsList({ examType }: { examType: ExamType }) {
  const { t } = useTranslation();
  const { role } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useExamsQuery();
  const isSuper = role === 'super_admin';

  // Names for the Scope column + the filter dropdowns: an exam row carries only ids. Fetched only
  // for a super_admin — a federation_admin sees one tenant and one sport, so neither filter would
  // have anything to choose between, and /v1/federations isn't theirs to call.
  const sportsQuery = useSportsQuery(isSuper);
  const federationsQuery = useFederationsQuery(isSuper);

  const [sportId, setSportId] = useState('all');
  const [federationId, setFederationId] = useState('all');
  // The list endpoint now hands a federation_admin the published platform ("Official") packs too, so
  // their referee-facing Rule tests page can show them. Those aren't authorable here (the API rejects
  // a non-super mutating a platform exam), so drop them from the authoring table — a fed_admin only
  // manages its own federation's exams. super_admin's list is already platform-only, so leave it be.
  // Also narrowed to this tab's type — Rule tests and Video tests are now separate tables, each
  // with its own authoring flow (question library vs. clip library), so mixing types back together
  // in one list would just reintroduce the "which kind is this row" column we dropped below.
  const typed = (data ?? []).filter(
    (e) => (isSuper || e.federation_id !== null) && e.type === examType,
  );

  const fedName = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of federationsQuery.data ?? []) map.set(f.id, f.name);
    return map;
  }, [federationsQuery.data]);
  const sportName = useMemo(() => {
    const map = new Map<string, string>();
    for (const sp of sportsQuery.data ?? []) map.set(sp.id, sp.name);
    return map;
  }, [sportsQuery.data]);

  // Options are derived from the exams actually listed, not the whole catalogue: a federation with
  // no exams of this type would otherwise sit in the dropdown and always filter to nothing (the
  // same rule the super_admin clips console uses).
  const federationOptions = useMemo<FilterOption[]>(() => {
    const seen = new Map<string, string>();
    let hasPlatform = false;
    for (const e of typed) {
      if (e.federation_id === null) hasPlatform = true;
      else if (!seen.has(e.federation_id)) seen.set(e.federation_id, fedName.get(e.federation_id) ?? t('admin.exams.list.unknownFallback'));
    }
    const named = Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return hasPlatform ? [{ value: PLATFORM, label: t('admin.exams.list.platformOption') }, ...named] : named;
  }, [typed, fedName, t]);

  const sportOptions = useMemo<FilterOption[]>(() => {
    const seen = new Map<string, string>();
    for (const e of typed) if (!seen.has(e.sport_id)) seen.set(e.sport_id, sportName.get(e.sport_id) ?? t('admin.exams.list.unknownFallback'));
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [typed, sportName, t]);

  const exams = useMemo(
    () =>
      typed.filter((e) => {
        if (sportId !== 'all' && e.sport_id !== sportId) return false;
        if (federationId === 'all') return true;
        return federationId === PLATFORM ? e.federation_id === null : e.federation_id === federationId;
      }),
    [typed, sportId, federationId],
  );

  const { page, setPage, pageCount, pageItems } = usePagination(exams, EXAMS_PAGE_SIZE);

  // usePagination only clamps a shrinking list, so a narrowed set would keep whatever page was
  // open — start each new filter at the top.
  useEffect(() => {
    setPage(1);
  }, [sportId, federationId, setPage]);

  if (isLoading) return <ListSkeleton rows={5} />;
  if (isError && !data) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(error, t('admin.exams.list.couldNotLoad'))}
      </div>
    );
  }
  // A super_admin's console spans every tenant, so its rows need a Scope column; a
  // federation_admin only ever sees its own federation's exams, where the column says nothing.
  const showScope = isSuper;
  const isFiltered = sportId !== 'all' || federationId !== 'all';
  // Only offer a dropdown once it has something to choose between.
  const showFilters = isSuper && (federationOptions.length > 1 || sportOptions.length > 1);

  const filterBar = showFilters ? (
    <div className="flex flex-wrap items-center gap-2">
      {federationOptions.length > 1 && (
        <FilterSelect
          label={t('admin.exams.list.filterByFederation')}
          value={federationId}
          onChange={setFederationId}
          options={[{ value: 'all', label: t('admin.clips.superAdminList.allFederations') }, ...federationOptions]}
        />
      )}
      {sportOptions.length > 1 && (
        <FilterSelect
          label={t('admin.exams.list.filterBySport')}
          value={sportId}
          onChange={setSportId}
          options={[{ value: 'all', label: t('admin.exams.list.allSports') }, ...sportOptions]}
        />
      )}
    </div>
  ) : null;

  if (exams.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {filterBar}
        <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-12 text-center">
          {/* "None yet, create one" is wrong when a filter is what emptied the list — say which
              it is, and offer the way back rather than leaving the bar looking broken. */}
          {isFiltered ? (
            <div className="flex flex-col items-center gap-2">
              <p className="text-sm text-muted">{t('admin.exams.list.noMatches')}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSportId('all');
                  setFederationId('all');
                }}
              >
                {t('admin.clips.list.clearFilters')}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted">{typeMeta(t, examType).empty}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {filterBar}
      {/* overflow-x-auto (not -hidden) so the row scrolls on a phone rather than crushing cells. */}
      <div className="overflow-x-auto rounded border border-line bg-surface">
        <table className="w-full min-w-[640px] text-left text-[13px]">
          <thead>
            <tr className="border-b border-line text-[11px] uppercase tracking-[0.05em] text-muted">
              <th className="px-4 py-2.5 font-semibold">{t('admin.exams.list.colExam')}</th>
              {showScope && <th className="px-4 py-2.5 font-semibold">{t('admin.exams.list.colScope')}</th>}
              <th className="px-4 py-2.5 font-semibold">{t('admin.exams.list.colLimit')}</th>
              <th className="px-4 py-2.5 font-semibold">{t('admin.exams.list.colStatus')}</th>
              <th className="px-4 py-2.5" aria-label={t('admin.exams.list.colActionsAria')} />
            </tr>
          </thead>
          <tbody>
            {pageItems.map((exam: Exam) => {
              const isPublished = exam.published_at !== null;
              return (
                <tr
                  key={exam.id}
                  className="group cursor-pointer border-b border-line transition-colors last:border-0 hover:bg-surface-2"
                  onClick={() => navigate(`/admin/exams/${exam.id}/edit`)}
                >
                  <td className="px-4 py-2.5 font-medium text-ink">{exam.title}</td>
                  {showScope && (
                    <td className="px-4 py-2.5 text-ink-2">
                      {/* Naming the tenant beats the bare word "Federation": the console now lists
                          every federation's exams, so which one is the whole point of the column. */}
                      {exam.federation_id === null
                        ? t('admin.exams.list.platformScope')
                        : (fedName.get(exam.federation_id) ?? t('admin.exams.list.unknownFederation'))}
                    </td>
                  )}
                  <td className="px-4 py-2.5 font-mono text-[12px] text-muted">{timeLimitLabel(t, exam.time_limit_sec)}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className={
                        'inline-flex h-[22px] items-center rounded-full px-2.5 text-[11px] font-medium ' +
                        (isPublished ? 'bg-accent/15 text-accent' : 'bg-surface-2 text-muted')
                      }
                    >
                      {isPublished ? t('admin.exams.list.published') : t('admin.exams.list.draft')}
                    </span>
                  </td>
                  {/* Stop row-navigation when using the inline actions. */}
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      {/* A shortcut straight to the Analytics tab. Not gated on published: an exam
                          pulled back from publication still has the sittings people already took,
                          and hiding them would strand the admin's own record. */}
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/exams/${exam.id}/results`)}>
                        {t('admin.exams.list.analytics')}
                      </Button>
                      <PublishToggle exam={exam} />
                      <DeleteExamDialog exam={exam} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <DataPagination page={page} pageCount={pageCount} onPageChange={setPage} />
    </div>
  );
}

interface ExamsPageProps {
  examType: ExamType;
}

// Two tabs, one per exam type — Rule tests and Video tests — rather than one list with a Type
// column. They're different enough authoring flows (question library vs. clip library) that
// keeping them apart avoids a "which kind is this row" glance on every table read, and each tab's
// New button now creates the RIGHT type directly instead of always drafting a rule test that the
// admin has to remember to flip to a clip exam in the editor. Separate routes (not local tab
// state), same reasoning as every other RouteTabs pairing in this app: an admin can link a
// colleague straight to "Video tests".
export function ExamsPage({ examType }: ExamsPageProps) {
  const { t } = useTranslation();
  const meta = typeMeta(t, examType);
  return (
    <>
      <PageHeader title={meta.title} sub={meta.sub} actions={<NewExamButton examType={examType} />} />
      <RouteTabs
        tabs={[
          { to: '/admin/exams', label: typeMeta(t, 'rule_test').tabLabel, end: true },
          { to: '/admin/exams/video-tests', label: typeMeta(t, 'video_clip_exam').tabLabel },
        ]}
      />
      <ExamsList examType={examType} />
    </>
  );
}
